/**
 * NoteShareSheetContainer - Manages share logic for notes
 *
 * Delegates common share modal behavior (user fetching, search, multi-select)
 * to the shared useShareModal hook, and manager display/auto-share logic to
 * useManagerShareRecipients. Adds note-specific functionality: fetching
 * existing share recipients, excluding already-shared users from the
 * selection list, filtering out the owner from the visible recipients,
 * and supporting access revocation with parent share-change notifications.
 *
 * Share and revoke mutations use Apollo `optimisticResponse` + `cache.modify`
 * for instant UI updates without a network round-trip after each mutation
 * (SE-4289). The `allOrgUsersRef` breaks the circular dependency between
 * the cache-aware `shareWithUser` (needs display names) and `useShareModal`
 * (provides org users only after it runs).
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import ShareModalView from "@/components/organisms/shared/ShareModalView";
import { useShareModal } from "@/components/organisms/shared/useShareModal";
import { useManagerShareRecipients } from "@/components/organisms/shared/useShareModal/useManagerShareRecipients";
import type { AvailableUserDisplayData } from "@/components/organisms/shared/useShareModal/useShareModal";
import { Box } from "@/components/atoms/Box";
import ShareRecipientsList from "@/features/reports/components/molecules/ShareRecipientsList";
import { useCurrentUser } from "@/features/auth";
import {
  buildOptimisticNoteShare,
  modifyListNoteSharesAdd,
  modifyListNoteSharesRemove,
} from "@/features/notes/utils/noteShareCacheUpdates";
import {
  RoleName,
  useNotesShareNoteMutation,
  useNotesListNoteSharesQuery,
  useNotesRevokeNoteShareMutation,
} from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";
import { formatError } from "@/utils/formatError";

/** Props for NoteShareSheetContainer */
interface Props {
  /** ID of the note to share */
  readonly noteId: string;
  /** Whether the sheet is visible */
  readonly isOpen: boolean;
  /** Callback when the sheet is dismissed */
  readonly onClose: () => void;
  /** Optional callback when shares change (share added or revoked) */
  readonly onShareChange?: () => void;
}

/**
 * Container for the note share modal - uses the shared useShareModal hook
 * for common logic and adds note-specific shares listing and revocation.
 * @param props - Component props
 * @param props.noteId - ID of the note to share
 * @param props.isOpen - Whether the sheet is visible
 * @param props.onClose - Callback when the sheet is dismissed
 * @param props.onShareChange - Optional callback when shares change
 * @returns Rendered ShareModalView with note-specific share/revoke wiring
 */
const NoteShareSheetContainer = ({
  noteId,
  isOpen,
  onClose,
  onShareChange,
}: Props) => {
  const { top } = useSafeAreaInsets();
  const toast = useToastNotification();
  const currentUser = useCurrentUser();
  const isMember = currentUser?.role?.name === RoleName.Member;

  const [shareNote] = useNotesShareNoteMutation();

  const { data: sharesData, loading: sharesLoading } =
    useNotesListNoteSharesQuery({
      variables: { noteId },
      skip: !isOpen || !noteId,
      fetchPolicy: "cache-and-network",
    });

  const [revokeShare, { loading: isRevoking }] =
    useNotesRevokeNoteShareMutation();

  const allShares = useMemo(
    () => sharesData?.listNoteShares ?? [],
    [sharesData]
  );

  const existingShareUserIds = useMemo(
    () => allShares.map(share => share.userId),
    [allShares]
  );

  const visibleShares = useMemo(
    () => allShares.filter(share => share.role !== "OWNER"),
    [allShares]
  );

  /** Wraps onClose to also refresh share counts in the parent Notes section */
  const handleClose = useCallback(() => {
    onShareChange?.();
    onClose();
  }, [onClose, onShareChange]);

  // Ref keeps the latest allOrgUsers list available to shareWithUser without
  // re-creating the callback (and thus re-running useShareModal) every time
  // the org users list changes.
  const allOrgUsersRef = useRef<readonly AvailableUserDisplayData[]>([]);

  const shareWithUser = useCallback(
    (targetUserId: string) => {
      const targetUser = allOrgUsersRef.current.find(
        u => u.userId === targetUserId
      );
      const optimisticShare = buildOptimisticNoteShare(
        targetUserId,
        targetUser?.displayName
      );
      return shareNote({
        variables: { input: { noteId, targetUserId } },
        optimisticResponse: { __typename: "Mutation", shareNote: true },
        update: cache => {
          modifyListNoteSharesAdd(cache, noteId, optimisticShare);
        },
      });
    },
    [shareNote, noteId]
  );

  const modal = useShareModal({
    isOpen,
    onClose: handleClose,
    entityId: noteId,
    shareFn: shareWithUser,
    entityLabel: "Note",
    excludeUserIds: existingShareUserIds,
    ...(isMember && { keepOrgRoles: ["MANAGER", "ADMIN"] }),
  });

  const {
    nonManagerShares,
    managerRecipients,
    shareWithUnsharedManagers: handleShareWithManagers,
  } = useManagerShareRecipients({
    allOrgUsers: modal.allOrgUsers,
    existingShareUserIds,
    modalOnShare: modal.onShare,
    shareWithUser,
    visibleShares,
  });

  const handleRevoke = useCallback(
    async (targetUserId: string) => {
      if (!noteId) return;
      try {
        await revokeShare({
          variables: { input: { noteId, targetUserId } },
          optimisticResponse: { __typename: "Mutation", revokeNoteShare: true },
          update: cache => {
            modifyListNoteSharesRemove(cache, noteId, targetUserId);
          },
        });
        toast.show({
          description: "Access revoked",
          action: "success",
        });
        onShareChange?.();
      } catch (error) {
        toast.show({
          description: formatError(error)?.message ?? "Failed to revoke access",
          action: "danger",
        });
      }
    },
    [noteId, revokeShare, toast, onShareChange]
  );

  // Sync the ref with the latest org users so shareWithUser sees fresh data.
  useEffect(() => {
    allOrgUsersRef.current = modal.allOrgUsers;
  }, [modal.allOrgUsers]);

  return (
    <ShareModalView
      {...modal}
      onShare={handleShareWithManagers}
      onClose={modal.onCancel}
      subtitle="Select teammates who should be able to view this note."
      topSafeArea={top}
    >
      {!sharesLoading &&
        (nonManagerShares.length > 0 || managerRecipients.length > 0) && (
          <Box className="border-t border-outline-subtle pt-4">
            <ShareRecipientsList
              shares={nonManagerShares}
              additionalRecipients={managerRecipients}
              onRevoke={handleRevoke}
              isRevoking={isRevoking}
              isDark={modal.isDark}
            />
          </Box>
        )}
    </ShareModalView>
  );
};

NoteShareSheetContainer.displayName = "NoteShareSheetContainer";

export default NoteShareSheetContainer;
